import { useCallback, useSyncExternalStore } from 'react';
import type { Side } from './partsRun';

// WHICH HAND OF A MIRROR PAIR IS BEING LOOKED AT, kept in ONE place for everything that draws
// this part.
//
// It was PartViewer's own state, which is the same mistake partMarks was written to undo and
// the same argument applies: a part is looked at in two viewers at once — the panel's and the
// modal that opens out of it — so each held a copy, and switching to the right-hand rail in
// one left the other still showing the left. Two viewers, one part, one hand.
//
// AND IT IS NOT ONLY THE VIEWERS ANY MORE, which is what forced it out. The controls beside
// them list the holes the manual bores in this part, and a mirror pair's holes come in two
// sets: the panel has to show the hand on screen, and a prop for that would thread the
// viewer's state through a component that has no other interest in it.
//
// LEFT IS THE FILE AND RIGHT IS ITS REFLECTION. A reconstruction is made from one photograph
// of one unit and nothing in the picture says which of the two was on the table, so calling
// the stored one Left is a convention — but it is a convention everything depends on, and it
// is why drilling is offered on the left hand alone: a bore cut at the right hand's
// coordinates into the file would be mirrored the moment the file was.
//
// NOT PERSISTED, like partMarks and unlike meshView. Which hand you last looked at is a
// working state, not a preference, and a part that opened tomorrow showing its right hand
// would be answering a question nobody had asked.
//
// ONE VALUE FOR EVERY PART, and not one per part. Only one part is open at a time in these
// viewers, and a value per part would keep an answer about a rail alive while a screw is on
// screen — which is a map that grows for the life of the tab and is read by nobody.
let side: Side = 'left';
const listeners = new Set<() => void>();

export const currentPartSide = (): Side => side;

export const setPartSide = (next: Side): void => {
  if (next === side) return;
  side = next;
  listeners.forEach((l) => l());
};

export const usePartSide = (): Side => {
  const subscribe = useCallback((cb: () => void) => {
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);
  return useSyncExternalStore(subscribe, () => side);
};
